import {
    IS_MASKED_SSN_VALUE,
    MAX_USER_AGE,
    MIN_PASSWORD_LENGTH,
    PATIENT_LEGAL_AGE,
    PRICE_SCALE_OF_TWO_DIGITS,
    PROCTOR_LEGAL_AGE,
    STRONG_PASSWORD_REGEX,
    VALID_EMAIL_REGEX,
} from "./validatorConstants";
import { AutoCompleteOption } from "../utils/appInterfaces/AppInterfaces";
import { DatesFormat } from "../utils/DateFormats";
import {
    convertNewDate,
    formatDateToUTC,
    getDateStringAsDate,
    parseStringDateWithFormat,
    subtractAndCheckIsBefore,
    subtractAndFormat,
    checkDateYears as checkDateYearsMethod,
    getDiffFromTodayAsYears,
    isDateIsValid,
    getCurrentDate,
    checkIfDateStringIsAfter,
} from "../utils/DateTimeUtils";

type ErrorMessage = {
    id: string;
    defaultMessage: string;
};

export const isRequired = (value: string | null | undefined): boolean => {
    return value === "" || value === null || value === undefined;
};

export const isRequiredPhoneNumber = (value: string, dialCode: string): boolean => {
    return value === "" || value.replace(/\D/g, "") === dialCode;
};

export const isRequiredSelect = (value: AutoCompleteOption | null): boolean => {
    return value === null || value.label === "";
};

export const isValidRegex = (value: string, pattern: RegExp): boolean => {
    return !pattern.test(value);
};

/**
 * The whole "is this password strong enough" policy in one call: minimum length AND all four
 * character classes. Always prefer this over `STRONG_PASSWORD_REGEX`, which covers only the
 * character classes and on its own accepts `Aa1!`.
 *
 * Note this returns true when the password is VALID — the opposite of `isValidRegex` above, which is
 * misnamed and returns true when the value does NOT match.
 *
 * The length used to live inside the regex as `(?=.{12,})`, which is what caused NG-8655: a separate
 * length test was later added in ignorance of it, the two collided, and a too-short password was told
 * to add character classes it already had. Keeping both here means one message can describe the whole
 * rule, and the 12 stays a named constant rather than a digit buried in a regex literal.
 *
 * The maximum length is NOT checked here — it has its own, more specific message at each call site,
 * which folding it in would replace with the generic strength message.
 */
export const isStrongPassword = (value: string): boolean => {
    return value.length >= MIN_PASSWORD_LENGTH && STRONG_PASSWORD_REGEX.test(value);
};

/**
 * BOTH OF THESE RETURN TRUE WHEN THE VALUE IS **OUT** OF RANGE.
 *
 * Read the name as `isOutOfRange` / `isOutOfLength`. `isValidLength("", 1, 128)` is `true` — the
 * empty string is *invalid*. They are predicates for "this is a problem", not for "this is fine",
 * and the names say the opposite of what they do.
 *
 * So a yup test accepting a range has to negate:
 *
 *     .test("length", "…between 1 and 2000…", (v) => !isValidLength(v, 1, 2000))   // accepts 1–2000
 *
 * while a component feeding an `error` prop must not:
 *
 *     error={isValidLength(code, 3, 80) && code !== ""}                            // true = show the error
 *
 * All ~40 call sites are currently consistent with this — 36 negate, 4 use the value directly as an
 * error flag — but nothing enforces it, and getting it backwards silently inverts a validator with
 * no type error and no test failure. Check which sense you need before adding a call.
 *
 * Not renamed here on purpose: the rename is mechanical but touches ~40 call sites across 25 files,
 * which is a change of its own rather than a rider on someone else's. See NG-8614.
 */
export const isValidLength = (value: string, minLength: number, maxLength: number): boolean => {
    return value.trim().length < minLength || value.trim().length > maxLength;
};

/** True when `value` is OUTSIDE [minValue, maxValue] — see the note on `isValidLength` above. */
export const isValidRange = (value: number, minValue: number, maxValue: number): boolean => {
    return value < minValue || value > maxValue;
};

export const isValidEmail = (value: string): { error: boolean; message: ErrorMessage } => {
    let errMessage = {} as ErrorMessage;
    const errRequired = isRequired(value);
    if (errRequired) {
        errMessage = {
            id: "common.email.error.required",
            defaultMessage: "Email cannot be empty",
        };
        return { error: errRequired, message: errMessage };
    }
    const errValid = isValidRegex(value, VALID_EMAIL_REGEX);
    if (errValid) {
        errMessage = {
            id: "common.email.error.is-valid",
            defaultMessage: "Invalid email address",
        };
        return { error: errValid, message: errMessage };
    }
    return { error: errValid, message: errMessage };
};

export const isRequiredAndValidRegex = (
    value: string,
    regExp: RegExp,
    errRequiredMessage: ErrorMessage,
    errValidMessage: ErrorMessage,
): { error: boolean; message: ErrorMessage } => {
    let errMessage = {} as ErrorMessage;
    const errRequired = isRequired(value);
    if (errRequired) {
        errMessage = errRequiredMessage;
        return { error: errRequired, message: errMessage };
    }
    const errValid = isValidRegex(value, regExp);
    if (errValid) {
        errMessage = errValidMessage;
        return { error: errValid, message: errMessage };
    }
    return { error: errValid, message: errMessage };
};
export const isRequiredAndValidLength = (
    value: string,
    minLength: number,
    maxLength: number,
    errRequiredMessage: ErrorMessage,
    errValidMessage: ErrorMessage,
): { error: boolean; message: ErrorMessage } => {
    let errMessage = {} as ErrorMessage;
    const errRequired = isRequired(value);
    if (errRequired) {
        errMessage = errRequiredMessage;
        return { error: errRequired, message: errMessage };
    }
    const errValid = isValidLength(value, minLength, maxLength);
    if (errValid) {
        errMessage = errValidMessage;
        return { error: errValid, message: errMessage };
    }
    return { error: errValid, message: errMessage };
};
export const isValidLengthField = (
    value: string,
    minLength: number,
    maxLength: number,
    errValidMessage: ErrorMessage,
): { error: boolean; message: ErrorMessage } => {
    let errMessage = {} as ErrorMessage;
    const errValid = isValidLength(value, minLength, maxLength);
    if (errValid) {
        errMessage = errValidMessage;
        return { error: errValid, message: errMessage };
    }
    return { error: errValid, message: errMessage };
};

export const checkIsValidURL = (url: string): boolean => {
    const result = url.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g);
    return result !== null;
};

export const getMinDate = () => subtractAndFormat(MAX_USER_AGE, "y", DatesFormat.newDefaultFormat);

export const checkPersonAge = (selectedDate: string, role: string) => {
    switch (role) {
        case "patient":
            return subtractAndCheckIsBefore(PATIENT_LEGAL_AGE, "y", selectedDate);
        case "proctor":
            return subtractAndCheckIsBefore(PROCTOR_LEGAL_AGE, "y", selectedDate);
        default:
            return undefined;
    }
};

export const checkUserYears = (selectedDate) => {
    const durationFromTodayYears: number = getDiffFromTodayAsYears(selectedDate);
    return durationFromTodayYears >= PATIENT_LEGAL_AGE && durationFromTodayYears <= MAX_USER_AGE;
};

export const checkProctorYears = (selectedDate: Date): boolean => {
    const durationFromTodayYears: number = getDiffFromTodayAsYears(selectedDate);
    return durationFromTodayYears >= PROCTOR_LEGAL_AGE && durationFromTodayYears <= MAX_USER_AGE;
};

export const isValidBirthDate = (date: string | null | Date, _format: string, legalAge?: number): boolean => {
    if (date) {
        const isValidDate = isDateIsValid(date);
        if (isValidDate) {
            let dateAsDate: Date;
            if (typeof date === "string") {
                dateAsDate = getDateStringAsDate(date);
            } else {
                dateAsDate = date;
            }
            if (legalAge && legalAge === PROCTOR_LEGAL_AGE) {
                return checkProctorYears(dateAsDate);
            }
            return checkUserYears(dateAsDate);
        }
        return false;
    }
    return false;
};

export const checkDateRange = (selectedDate: string) => {
    const utc = formatDateToUTC(selectedDate);
    return utc.isValid() && utc.isSameOrAfter(parseStringDateWithFormat(getMinDate(), DatesFormat.newDefaultFormat)) && utc.isSameOrBefore(convertNewDate());
};

export const checkDateYears = (selectedDate: string) => {
    return checkDateYearsMethod(selectedDate, "1000-01-01");
};

export const isValidPrice = (value: string): boolean => {
    return !isValidRegex(value, PRICE_SCALE_OF_TWO_DIGITS);
};

export const isDateInFuture = (item: Date): boolean => {
    const currentDate = getCurrentDate();
    const res = checkIfDateStringIsAfter(item, currentDate);
    return !res;
};

export const isMaskedSSNValue = (value: string): boolean => {
    return !isValidRegex(value, IS_MASKED_SSN_VALUE);
};
